import { useEffect, useState } from "react";
import Layout from "@/components/layout/Layout";
import { listPlans, runPlan, listAssets, listClients, listSites, createPlan, updatePlan } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CalendarClock, PlayCircle, Repeat, Plus, Pencil, Power } from "lucide-react";
import { toast } from "sonner";

export default function Maintenance() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [assets, setAssets] = useState([]);
  const [clients, setClients] = useState([]);
  const [sites, setSites] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [openNew, setOpenNew] = useState(false);
  const [editPlan, setEditPlan] = useState(null);

  const isAdmin = user?.role === "admin";
  const canRun = isAdmin || user?.role === "technician";

  const load = async () => {
    const [p, a, c, s] = await Promise.all([
      listPlans(showInactive),
      listAssets({ include_inactive: true }),
      listClients(),
      listSites(),
    ]);
    setPlans(p);
    setAssets(a);
    setClients(c);
    setSites(s);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [showInactive]);

  const run = async (id) => {
    try {
      await runPlan(id);
      toast.success("PM work order generated");
      load();
    } catch { toast.error("Failed"); }
  };

  const toggleActive = async (p) => {
    const next = p.active === false ? true : false;
    if (!next && !window.confirm(`Deactivate plan "${p.name}"?`)) return;
    await updatePlan(p.id, { active: next });
    toast.success(next ? "Plan activated" : "Plan deactivated");
    load();
  };

  return (
    <Layout
      title="Maintenance Plans"
      breadcrumbs={["Operations", "PPM"]}
      actions={
        isAdmin && (
          <Dialog open={openNew} onOpenChange={setOpenNew}>
            <DialogTrigger asChild>
              <Button data-testid="new-plan-btn" className="rounded-sm bg-orange-500 hover:bg-orange-600 text-white font-semibold">
                <Plus className="h-4 w-4 mr-1" /> New PM
              </Button>
            </DialogTrigger>
            <PlanDialog
              clients={clients}
              sites={sites}
              assets={assets}
              onSaved={() => { setOpenNew(false); load(); }}
            />
          </Dialog>
        )
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {plans.map((p) => {
          const asset = assets.find((a) => a.id === p.asset_id);
          const client = clients.find((c) => c.id === p.client_id);
          const site = sites.find((s) => s.id === p.site_id);
          const next = new Date(p.next_due);
          const now = new Date();
          const overdue = next < now && p.active !== false;
          const daysAway = Math.round((next - now) / 86400000);
          return (
            <div
              key={p.id}
              data-testid={`plan-${p.id}`}
              className={`engineered-card p-5 ${p.active === false ? "opacity-60" : ""}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500 flex items-center gap-2">
                    <span className="inline-flex items-center gap-1"><Repeat className="h-3 w-3" /> Every {p.frequency_days} days</span>
                    {p.active === false && <span className="px-2 py-0.5 bg-red-50 border border-red-300 rounded-sm text-red-700">INACTIVE</span>}
                  </div>
                  <h3 className="font-display font-bold text-xl text-zinc-950 mt-1">{p.name}</h3>
                  <div className="text-xs text-zinc-600 mt-1 font-mono space-y-0.5">
                    {client && <div>Client: {client.name}</div>}
                    {site && <div>Site: {site.name}</div>}
                    {asset && <div>Asset: {asset.tag} · {asset.name}</div>}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div className={`px-3 py-1.5 rounded-sm border text-[10px] font-bold uppercase tracking-wider ${overdue ? "bg-red-50 border-red-300 text-red-700" : daysAway < 7 ? "bg-yellow-50 border-yellow-300 text-yellow-800" : "bg-emerald-50 border-emerald-300 text-emerald-700"}`}>
                    <CalendarClock className="h-3 w-3 inline mr-1" />
                    {overdue ? "Overdue" : `${daysAway}d`}
                  </div>
                  {isAdmin && (
                    <div className="flex gap-1">
                      <button data-testid={`edit-plan-${p.id}`} onClick={() => setEditPlan(p)} className="h-7 w-7 rounded-sm border border-zinc-300 bg-white hover:border-orange-500 text-zinc-700 flex items-center justify-center" title="Edit">
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button data-testid={`toggle-plan-${p.id}`} onClick={() => toggleActive(p)} className={`h-7 w-7 rounded-sm border flex items-center justify-center ${p.active === false ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "bg-white border-zinc-300 text-zinc-700 hover:bg-red-50 hover:text-red-700"}`} title={p.active === false ? "Activate" : "Deactivate"}>
                        <Power className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-4 space-y-1.5 max-h-32 overflow-y-auto">
                {(p.tasks || []).map((t, i) => (
                  <div key={`${p.id}-task-${i}`} className="text-sm text-zinc-700 flex items-start gap-2">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-sm bg-orange-500 shrink-0" />
                    <span>{t}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-zinc-200 flex items-center justify-between text-xs">
                <div className="font-mono text-zinc-600">Next: {next.toLocaleDateString()}</div>
                {canRun && p.active !== false && (
                  <Button size="sm" data-testid={`run-plan-${p.id}`} onClick={() => run(p.id)} className="bg-orange-500 hover:bg-orange-600 rounded-sm font-bold">
                    <PlayCircle className="h-4 w-4 mr-1" /> Generate PM
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {plans.length === 0 && (
        <div className="text-center py-10 text-sm text-zinc-500">No maintenance plans yet.</div>
      )}
      {isAdmin && (
        <div className="mt-4 text-xs text-zinc-500">
          <label className="inline-flex items-center gap-2">
            <input data-testid="plans-show-inactive" type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
            Show inactive plans
          </label>
        </div>
      )}
      <Dialog open={!!editPlan} onOpenChange={(o) => !o && setEditPlan(null)}>
        {editPlan && (
          <PlanDialog
            plan={editPlan}
            clients={clients}
            sites={sites}
            assets={assets}
            onSaved={() => { setEditPlan(null); load(); }}
          />
        )}
      </Dialog>
    </Layout>
  );
}

function PlanDialog({ plan, clients, sites, assets, onSaved }) {
  const editing = !!plan;
  const [form, setForm] = useState(
    plan
      ? {
          name: plan.name,
          client_id: plan.client_id,
          site_id: plan.site_id,
          asset_id: plan.asset_id || "",
          frequency_days: plan.frequency_days,
          next_due: (plan.next_due || "").slice(0, 10),
          assignee_name: plan.assignee_name || "",
          tasks: (plan.tasks || []).join("\n"),
        }
      : {
          name: "",
          client_id: "",
          site_id: "",
          asset_id: "",
          frequency_days: 90,
          next_due: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10),
          assignee_name: "Demo Technician",
          tasks: "",
        }
  );
  const [busy, setBusy] = useState(false);

  const clientSites = sites.filter((s) => s.client_id === form.client_id);
  const siteAssets = assets.filter((a) => a.site_id === form.site_id);

  const submit = async () => {
    if (!form.name || !form.client_id || !form.site_id || !form.asset_id) {
      toast.error("Name, client, site and asset are required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name,
        client_id: form.client_id,
        site_id: form.site_id,
        asset_id: form.asset_id,
        frequency_days: Number(form.frequency_days) || 90,
        next_due: new Date(form.next_due).toISOString(),
        assignee_name: form.assignee_name || null,
        tasks: form.tasks.split("\n").map((t) => t.trim()).filter(Boolean),
      };
      if (editing) {
        await updatePlan(plan.id, payload);
        toast.success("Plan updated");
      } else {
        await createPlan(payload);
        toast.success("Plan created");
      }
      onSaved();
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  return (
    <DialogContent className="rounded-sm max-w-2xl">
      <DialogHeader>
        <DialogTitle className="font-display text-2xl">{editing ? `Edit Plan · ${plan.name}` : "New Preventive Maintenance Plan"}</DialogTitle>
      </DialogHeader>
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Label className="text-xs font-semibold uppercase tracking-wider">Plan name *</Label>
          <Input data-testid="plan-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm h-11" placeholder="e.g. Chiller Quarterly PM" />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider">Client *</Label>
          <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v, site_id: "", asset_id: "" })}>
            <SelectTrigger data-testid="plan-client" className="rounded-sm h-11"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider">Site *</Label>
          <Select value={form.site_id} onValueChange={(v) => setForm({ ...form, site_id: v, asset_id: "" })} disabled={!form.client_id}>
            <SelectTrigger data-testid="plan-site" className="rounded-sm h-11"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{clientSites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="col-span-2">
          <Label className="text-xs font-semibold uppercase tracking-wider">Asset *</Label>
          <Select value={form.asset_id} onValueChange={(v) => setForm({ ...form, asset_id: v })} disabled={!form.site_id}>
            <SelectTrigger data-testid="plan-asset" className="rounded-sm h-11"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{siteAssets.map((a) => <SelectItem key={a.id} value={a.id}>{a.tag} · {a.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider">Frequency (days) *</Label>
          <Input data-testid="plan-freq" type="number" min="1" value={form.frequency_days} onChange={(e) => setForm({ ...form, frequency_days: e.target.value })} className="rounded-sm h-11" />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider">Next due *</Label>
          <Input data-testid="plan-next-due" type="date" value={form.next_due} onChange={(e) => setForm({ ...form, next_due: e.target.value })} className="rounded-sm h-11" />
        </div>
        <div className="col-span-2">
          <Label className="text-xs font-semibold uppercase tracking-wider">Default assignee</Label>
          <Input value={form.assignee_name} onChange={(e) => setForm({ ...form, assignee_name: e.target.value })} className="rounded-sm h-11" />
        </div>
        <div className="col-span-2">
          <Label className="text-xs font-semibold uppercase tracking-wider">Tasks (one per line)</Label>
          <Textarea data-testid="plan-tasks" rows={5} value={form.tasks} onChange={(e) => setForm({ ...form, tasks: e.target.value })} className="rounded-sm" placeholder={"Check refrigerant pressures\nInspect oil level\nClean condenser coils"} />
        </div>
      </div>
      <DialogFooter>
        <Button onClick={submit} disabled={busy} data-testid="plan-submit" className="bg-orange-500 hover:bg-orange-600 rounded-sm font-bold">
          {busy ? "Saving…" : editing ? "Save Plan" : "Create Plan"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
